// Weigh-in lifecycle RPC client wrappers.
//
// Tier-2 write boundary (migrations 195/196): weigh_ins and weigh_in_sessions
// no longer grant INSERT/UPDATE/DELETE to authenticated. Every mutation other
// than the pig/broiler fresh-session batch create (submit_weigh_in_session_batch,
// mig 035/193, called directly via useOfflineRpcSubmit.js) and the delete path
// (delete_weigh_in_entry / delete_weigh_in_session, src/lib/weighInDeleteApi.js)
// goes through the wrappers below. All throw on RPC error, matching
// src/lib/dailyReportsApi.js's convention — none of these need the raw
// {data, error} offline-classification contract submitCattleDailyReport/
// submitSheepDailyReport use, because cattle/sheep weigh-in sessions have no
// offline queue (see WeighInsWebform.jsx's header comment: cattle/sheep paths
// and the completion flow remain online-direct and unchanged).

import type {RpcEnvelope, SupabaseClient} from './supabaseTypes.js';

// Every payload below crosses the boundary as jsonb the RPC validates itself,
// so the client side of the contract is "an object of caller-supplied columns"
// — the allowed keys live in migrations 195/196/197 and are enforced there.
export type WeighInPayload = Record<string, unknown>;

export async function createWeighInSession(sb: SupabaseClient, session: WeighInPayload): Promise<RpcEnvelope> {
  if (!sb) throw new Error('createWeighInSession: sb required');
  const {data, error} = await sb.rpc('create_weigh_in_session', {p_session: session});
  if (error) throw new Error(error.message || String(error));
  return data;
}

export async function addWeighInEntry(
  sb: SupabaseClient,
  sessionId: string,
  entry: WeighInPayload,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('addWeighInEntry: sb required');
  const {data, error} = await sb.rpc('add_weigh_in_entry', {p_session_id: sessionId, p_entry: entry});
  if (error) throw new Error(error.message || String(error));
  return data;
}

export async function updateWeighInEntry(
  sb: SupabaseClient,
  entryId: string,
  patch: WeighInPayload,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('updateWeighInEntry: sb required');
  const {data, error} = await sb.rpc('update_weigh_in_entry', {p_entry_id: entryId, p_patch: patch});
  if (error) throw new Error(error.message || String(error));
  return data;
}

export async function replaceWeighInSessionEntries(
  sb: SupabaseClient,
  sessionId: string,
  entries: WeighInPayload[],
  notes?: string | null,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('replaceWeighInSessionEntries: sb required');
  const {data, error} = await sb.rpc('replace_weigh_in_session_entries', {
    p_session_id: sessionId,
    p_entries: entries,
    p_notes: notes ?? null,
  });
  if (error) throw new Error(error.message || String(error));
  return data;
}

export async function updateWeighInSession(
  sb: SupabaseClient,
  sessionId: string,
  patch: WeighInPayload,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('updateWeighInSession: sb required');
  const {data, error} = await sb.rpc('update_weigh_in_session', {p_session_id: sessionId, p_patch: patch});
  if (error) throw new Error(error.message || String(error));
  return data;
}

export async function completeWeighInSession(sb: SupabaseClient, sessionId: string): Promise<RpcEnvelope> {
  if (!sb) throw new Error('completeWeighInSession: sb required');
  const {data, error} = await sb.rpc('complete_weigh_in_session', {p_session_id: sessionId});
  if (error) throw new Error(error.message || String(error));
  return data;
}

export async function reopenWeighInSession(sb: SupabaseClient, sessionId: string): Promise<RpcEnvelope> {
  if (!sb) throw new Error('reopenWeighInSession: sb required');
  const {data, error} = await sb.rpc('reopen_weigh_in_session', {p_session_id: sessionId});
  if (error) throw new Error(error.message || String(error));
  return data;
}

// Tier-2 write boundary (migration 197). Sheep-only — cattle_comments is a
// separate, not-yet-converted domain; WeighInSessionPage.jsx's reconcileNewTag
// keeps its direct cattle_comments update unchanged and calls this only for
// species === 'sheep'.
export async function reconcileSheepWeighInComment(
  sb: SupabaseClient,
  referenceId: string,
  sheepId: string,
  sheepTag: string,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('reconcileSheepWeighInComment: sb required');
  const {data, error} = await sb.rpc('reconcile_sheep_weigh_in_comment', {
    p_reference_id: referenceId,
    p_sheep_id: sheepId,
    p_sheep_tag: sheepTag,
  });
  if (error) throw new Error(error.message || String(error));
  return data;
}

// Tier-2 write boundary (migration 197): management/admin only, matching the
// bulk-import tool's own admin-only reachability.
export async function importSheepComment(sb: SupabaseClient, record: WeighInPayload): Promise<RpcEnvelope> {
  if (!sb) throw new Error('importSheepComment: sb required');
  const {data, error} = await sb.rpc('import_sheep_comment', {p_record: record});
  if (error) throw new Error(error.message || String(error));
  return data;
}
